import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  Avatar,
  Badge,
  Em,
  Flex,
  IconButton,
  Skeleton,
  Text,
} from "@radix-ui/themes";
import { TrashIcon, Pencil1Icon } from "@radix-ui/react-icons";
import {
  getMemberRole,
  Member,
  PermissionDisplay,
  Role,
} from "../../api/member/Member.model";
import { useDeleteMember } from "../../api/member/Member.api";
import { radixColors } from "@/components/radixColors";
import { EditMemberDialog } from "./EditMemberDialog";

interface MemberItemProps {
  index: number;
  member: Member;
  isAdmin: boolean;
  refetch: () => void;
}

export function MemberItem({
  index,
  member,
  refetch,
  isAdmin,
}: MemberItemProps) {
  const { mutate: deleteUserMutate, isLoading } = useDeleteMember(member.id);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const role = useMemo(() => getMemberRole(member), [member]);

  const handleDelete = useCallback(() => {
    deleteUserMutate(
      {},
      {
        onSuccess: () => {
          toast(`Member ${member.email} deleted`);
          refetch();
        },
      }
    );
  }, [deleteUserMutate]);

  const { name, email, permissions } = member;
  const hasName = name != null;

  return (
    <Flex height={"40px"} align={"center"} gap={"3"}>
      <Avatar size={"2"} fallback={email[0]} color={radixColors[index]} />

      {hasName ? (
        <>
          <Text
            size={"3"}
            color={"gray"}
            weight={"medium"}
            highContrast
            truncate
            style={{ width: "240px" }}
          >
            {name}
          </Text>

          <Text size={"2"} color={"gray"}>
            <Em>{email}</Em>
          </Text>
        </>
      ) : (
        <Text
          size={"3"}
          color={"gray"}
          weight={"medium"}
          highContrast
          truncate
          style={{ width: "240px" }}
        >
          {email}
        </Text>
      )}

      <Flex
        flexGrow={"1"}
        pr={"16px"}
        justify={"end"}
        align={"center"}
        gap={"2"}
      >
        <Badge color={"gray"}>{role}</Badge>

        {role === Role.MEMBER &&
          permissions.map((permission) => (
            <Badge color={"blue"}>{PermissionDisplay[permission]}</Badge>
          ))}

        {isAdmin && (
          <>
            <IconButton
              size={"2"}
              variant={"ghost"}
              color={"gray"}
              onClick={() => setEditDialogOpen(true)}
            >
              <Pencil1Icon />
            </IconButton>
            <IconButton
              size={"2"}
              variant={"ghost"}
              color={"red"}
              onClick={handleDelete}
              loading={isLoading}
              disabled={isLoading}
            >
              <TrashIcon />
            </IconButton>
          </>
        )}
      </Flex>

      <EditMemberDialog
        member={member}
        refetch={refetch}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
      />
    </Flex>
  );
}
